/**
 * Demo content — the simulated data behind the mock backend. Field names match
 * the real API response shapes (see demo-routes for the endpoint mapping).
 * Narrative reused from the build brief: Sanne/Daan team, Acme/Nova/Bloom
 * brands, the "Summer campaign +15%" governance moment.
 *
 * Everything here is fictional. No real accounts, tokens, or PII.
 */

const now = Date.now();
export const iso = (msAgo: number): string => new Date(now - msAgo).toISOString();
export const MIN = 60_000;
export const HOUR = 60 * MIN;
export const DAY = 24 * HOUR;

/* ----------------------------- capabilities ----------------------------- */
// Full owner capability set (permissions.ts) so every card/tab/button renders.
export const DEMO_CAPABILITIES: string[] = [
  'clients.view',
  'clients.create',
  'clients.delete',
  'clients.manage',
  'clients.connect_platform',
  'proposals.view',
  'proposals.create',
  'proposals.propose',
  'proposals.approve',
  'proposals.deny',
  'team.view',
  'team.invite',
  'team.delete',
  'skills.view',
  'skills.install',
  'skills.create_org',
  'skills.delete',
  'billing.view',
  'billing.manage',
  'integrations.manage',
  'mcp.access.view',
  'mcp.access.manage',
  'notifications.view',
  'notifications.configure',
  'stats.view',
  'stats.perUser',
  'audit.view',
  'meta_activity.view',
  'mcp_calls.view',
  'settings.manage_org',
  // Client-report portal group (owner holds all of these).
  'portal.reports.review',
  'portal.reports.approve',
  'portal.branding.manage',
  'portal.kpis.manage',
  'portal.clients.manage',
  'portal.domains.manage',
  'portal.email.manage',
  'portal.settings.manage',
];

/* -------------------------------- brands -------------------------------- */
export interface DemoBrand {
  id: number;
  slug: string;
  name: string;
  webshop_url: string;
  sector: string;
}

export const DEMO_BRANDS: DemoBrand[] = [
  { id: 1, slug: 'acme', name: 'Acme B.V.', webshop_url: 'acme.example', sector: 'E-commerce' },
  { id: 2, slug: 'nova-sneakers', name: 'Nova Sneakers', webshop_url: 'novasneakers.example', sector: 'Retail · Fashion' },
  { id: 3, slug: 'bloom-cosmetics', name: 'Bloom Cosmetics', webshop_url: 'bloomcosmetics.example', sector: 'Beauty · DTC' },
  { id: 4, slug: 'lumen-audio', name: 'Lumen Audio', webshop_url: 'lumenaudio.example', sector: 'Consumer electronics' },
];

export const DEMO_ACTIVE_SHOP = 'acme';

export const DEMO_ACCESSIBLE_SHOPS = DEMO_BRANDS.map((b) => ({
  slug: b.slug,
  name: b.name,
  permission_level: 'approve',
  brand_id: b.id,
  brand_slug: b.slug,
  brand_name: b.name,
}));

export const DEMO_ALL_SHOPS = DEMO_BRANDS.map((b) => ({
  id: b.id,
  slug: b.slug,
  name: b.name,
  webshop_url: b.webshop_url,
}));

/* ------------------------------- identity ------------------------------- */
export const DEMO_IDENTITY = {
  type: 'owner',
  employee_id: 'emp-owner-1',
  organisation_id: 'org-demo',
  email: 'alex@aurora-agency.io',
  name: 'Alex Mercer',
  role: 'owner',
  capabilities: DEMO_CAPABILITIES,
  account_type: 'agency',
  accessible_shops: DEMO_ACCESSIBLE_SHOPS,
  accessible_brands: DEMO_ACCESSIBLE_SHOPS,
  needs_legacy_reconnect: [],
};

/* ------------------------------ seller-init ----------------------------- */
export const DEMO_SELLER_INIT = {
  success: true,
  message: 'ok',
  data: {
    user_data: {
      browser_language: 'en_US',
      device_country: { countryCode: 'NL' },
    },
    app_data: { countries: [], static_base_url: 'https://static.adup.io' },
    merchant: {
      id: 'm-demo',
      slug: 'aurora-agency',
      name: 'Aurora Agency',
      is_white_label: false,
      settings: {},
    },
    seller: {
      seller_id: 1,
      user_id: 1,
      roles: [{ name: 'agency' }],
      first_name: 'Alex',
      last_name: 'Mercer',
      email: 'alex@aurora-agency.io',
    },
    shop: DEMO_BRANDS.map((b) => ({
      id: b.id,
      slug: b.slug,
      name: b.name,
      is_active: 1,
      currency: 'EUR',
      currency_symbol: '€',
      settings: { general: { webshop_url: b.webshop_url } },
      stats: {},
    })),
    subscription: [],
  },
};

/* ------------------------------ employees ------------------------------- */
export const DEMO_EMPLOYEES = [
  {
    id: 'emp-owner-1',
    email: 'alex@aurora-agency.io',
    name: 'Alex Mercer',
    role: 'owner',
    is_team_lead: false,
    active: true,
    assigned_shops: DEMO_ACCESSIBLE_SHOPS,
    group_id: null,
    invitation_accepted_at: iso(40 * DAY),
  },
  {
    id: 'emp-mara',
    email: 'mara@aurora-agency.io',
    name: 'Mara Jansen',
    role: 'team_lead',
    is_team_lead: true,
    active: true,
    assigned_shops: DEMO_ACCESSIBLE_SHOPS,
    group_id: 'grp-perf',
    invitation_accepted_at: iso(30 * DAY),
  },
  {
    id: 'emp-daan',
    email: 'daan@aurora-agency.io',
    name: 'Daan Bakker',
    role: 'manager',
    is_team_lead: false,
    active: true,
    assigned_shops: DEMO_ACCESSIBLE_SHOPS.slice(0, 3),
    group_id: 'grp-perf',
    invitation_accepted_at: iso(21 * DAY),
  },
  {
    id: 'emp-sanne',
    email: 'sanne@aurora-agency.io',
    name: 'Sanne de Vries',
    role: 'analyst',
    is_team_lead: false,
    active: true,
    assigned_shops: DEMO_ACCESSIBLE_SHOPS.slice(0, 2),
    group_id: 'grp-perf',
    invitation_accepted_at: iso(14 * DAY),
  },
  {
    id: 'emp-tom',
    email: 'tom@aurora-agency.io',
    name: 'Tom Visser',
    role: 'read_only',
    is_team_lead: false,
    active: true,
    assigned_shops: DEMO_ACCESSIBLE_SHOPS.slice(0, 1),
    group_id: null,
    invitation_accepted_at: iso(9 * DAY),
  },
  {
    id: 'emp-lena',
    email: 'lena@aurora-agency.io',
    name: 'Lena Smit',
    role: 'analyst',
    is_team_lead: false,
    active: true,
    assigned_shops: [],
    group_id: null,
    invitation_accepted_at: null, // pending invite
  },
];

export const DEMO_GROUPS = [
  {
    id: 'grp-perf',
    name: 'Performance Pod',
    role: 'manager',
    team_lead_id: 'emp-mara',
    team_lead_name: 'Mara Jansen',
    member_count: 3,
  },
];

/* -------------------------------- roles --------------------------------- */
const roleCap = (caps: string[]) => caps;
export const DEMO_ROLES = {
  data: [
    {
      id: 'role-owner',
      key: 'owner',
      name: 'Owner',
      description: 'Full access to everything in the organisation.',
      capabilities: roleCap(DEMO_CAPABILITIES),
      brand_access: 'all',
      is_system: true,
      is_assignable: true,
    },
    {
      id: 'role-team-lead',
      key: 'team_lead',
      name: 'Team lead',
      description: 'Approves writes and manages the team & roles.',
      capabilities: roleCap([
        'clients.view', 'proposals.view', 'proposals.approve', 'proposals.deny',
        'team.view', 'team.invite', 'stats.view', 'stats.perUser', 'audit.view',
      ]),
      brand_access: 'all',
      is_system: true,
      is_assignable: true,
    },
    {
      id: 'role-manager',
      key: 'manager',
      name: 'Manager',
      description: 'Approves or blocks writes on assigned brands.',
      capabilities: roleCap([
        'clients.view', 'proposals.view', 'proposals.approve', 'proposals.deny', 'stats.view',
      ]),
      brand_access: 'assigned',
      is_system: true,
      is_assignable: true,
    },
    {
      id: 'role-analyst',
      key: 'analyst',
      name: 'Analyst',
      description: 'Proposes changes — every write goes to an approver.',
      capabilities: roleCap(['clients.view', 'proposals.view', 'proposals.create', 'proposals.propose']),
      brand_access: 'assigned',
      is_system: true,
      is_assignable: true,
    },
    {
      id: 'role-read-only',
      key: 'read_only',
      name: 'Read-only',
      description: 'Views dashboards & reports only.',
      capabilities: roleCap(['clients.view', 'proposals.view', 'stats.view']),
      brand_access: 'assigned',
      is_system: true,
      is_assignable: true,
    },
  ],
  all_capabilities: DEMO_CAPABILITIES.map((key) => ({ key, name: key })),
  all_platforms: [],
};

/* ------------------------------ proposals ------------------------------- */
// The hero governance moment is `prop-1` (Summer campaign +15%, pending review).
export const DEMO_PROPOSALS_SEED = [
  {
    // A client-report review item — deep-links into the report workbench.
    id: 'prop-report-jun',
    shop_id: 1,
    shop: { slug: 'acme', name: 'Acme B.V.' },
    action_type_id: 'client_report',
    action_type: { action_key: 'client_report', name: 'Client report' },
    platform: null,
    category: 'report',
    entity_id: 'rep-2026-06-acme',
    entity_name: 'Acme B.V. — June 2026 performance',
    current_value: null,
    proposed_value: null,
    reasoning:
      'TARA drafted the June performance report from Google Ads, Meta and TikTok. Review it, tweak the wording, then approve to publish it to the client portal.',
    confidence_score: null,
    change_percentage: null,
    risk_level: 'low',
    status: 'pending_review',
    auto_approved: false,
    execution_type: 'client_report',
    tool_name: null,
    requested_by_employee_id: null,
    metadata: {
      report_id: 'rep-2026-06-acme',
      report_version: 2,
      title: 'Acme B.V. — June 2026 performance',
      brand: 'Acme B.V.',
      shop_slug: 'acme',
      period_label: 'June 2026',
      created_by_type: 'ai',
      report_review_path: '/client-reports/rep-2026-06-acme',
    },
    created_at: iso(8 * MIN),
    updated_at: iso(8 * MIN),
  },
  {
    id: 'prop-1',
    shop_id: 1,
    shop: { slug: 'acme', name: 'Acme B.V.' },
    action_type_id: 'budget_update',
    action_type: { action_key: 'budget_update', name: 'Budget change' },
    platform: 'facebook_ads',
    category: 'budget',
    entity_id: 'camp-778',
    entity_name: 'Summer campaign',
    current_value: { daily_budget: 200, currency: 'EUR' },
    proposed_value: { daily_budget: 230, currency: 'EUR' },
    reasoning:
      'ROAS held at 3.4 over the last 14 days with budget capped out by 6pm daily. A 15% raise captures unmet demand while staying within the target ROAS band.',
    confidence_score: 0.86,
    change_percentage: 15,
    risk_level: 'medium',
    status: 'pending_review',
    auto_approved: false,
    execution_type: 'platform_api',
    tool_name: 'update_budget',
    requested_by_employee_id: 'emp-sanne',
    created_at: iso(18 * MIN),
    updated_at: iso(18 * MIN),
  },
  {
    id: 'prop-2',
    shop_id: 1,
    shop: { slug: 'acme', name: 'Acme B.V.' },
    action_type_id: 'bid_update',
    action_type: { action_key: 'bid_update', name: 'Bid adjustment' },
    platform: 'google_ads',
    category: 'bidding',
    entity_id: 'adg-221',
    entity_name: 'Brand – Search',
    current_value: { target_roas: 3.2 },
    proposed_value: { target_roas: 3.0 },
    reasoning:
      'Bid shift below the 10% threshold while ROAS > 3.0 — cleared automatically by your KPI rule.',
    change_percentage: 6,
    risk_level: 'low',
    status: 'approved',
    auto_approved: true,
    reviewed_at: iso(22 * MIN),
    execution_type: 'platform_api',
    tool_name: 'update_bids',
    requested_by_employee_id: 'emp-sanne',
    created_at: iso(24 * MIN),
    updated_at: iso(22 * MIN),
  },
  {
    id: 'prop-3',
    shop_id: 2,
    shop: { slug: 'nova-sneakers', name: 'Nova Sneakers' },
    action_type_id: 'status_update',
    action_type: { action_key: 'status_update', name: 'Pause ad set' },
    platform: 'facebook_ads',
    category: 'status',
    entity_id: 'adset-91',
    entity_name: 'Retargeting – 30d',
    current_value: { status: 'ACTIVE' },
    proposed_value: { status: 'PAUSED' },
    reasoning:
      'Frequency 5.2 and CTR down 38% week-over-week — fatigue. Pause to protect spend until new creative ships.',
    change_percentage: 0,
    risk_level: 'low',
    status: 'pending_review',
    auto_approved: false,
    execution_type: 'official_mcp',
    tool_name: 'pause_adset',
    requested_by_employee_id: 'emp-sanne',
    created_at: iso(2 * HOUR),
    updated_at: iso(2 * HOUR),
  },
  {
    id: 'prop-4',
    shop_id: 3,
    shop: { slug: 'bloom-cosmetics', name: 'Bloom Cosmetics' },
    action_type_id: 'budget_update',
    action_type: { action_key: 'budget_update', name: 'Budget change' },
    platform: 'tiktok_ads',
    category: 'budget',
    entity_id: 'camp-455',
    entity_name: 'Spring Launch',
    current_value: { daily_budget: 150, currency: 'EUR' },
    proposed_value: { daily_budget: 210, currency: 'EUR' },
    reasoning:
      'Strong hook rate (28%) and CVR above account average. Proposing a 40% raise — flagged high-risk for human review because it exceeds the 25% guardrail.',
    change_percentage: 40,
    risk_level: 'high',
    status: 'pending_review',
    auto_approved: false,
    execution_type: 'platform_api',
    tool_name: 'update_budget',
    requested_by_employee_id: 'emp-lena',
    created_at: iso(3 * HOUR),
    updated_at: iso(3 * HOUR),
  },
  {
    id: 'prop-5',
    shop_id: 4,
    shop: { slug: 'lumen-audio', name: 'Lumen Audio' },
    action_type_id: 'budget_update',
    action_type: { action_key: 'budget_update', name: 'Budget change' },
    platform: 'linkedin_ads',
    category: 'budget',
    entity_id: 'camp-12',
    entity_name: 'ABM – Enterprise',
    current_value: { daily_budget: 120, currency: 'EUR' },
    proposed_value: { daily_budget: 108, currency: 'EUR' },
    reasoning:
      'CPL up 22% with flat pipeline. Trim 10% and reallocate to the better-performing creative set.',
    change_percentage: -10,
    risk_level: 'low',
    status: 'proposed',
    auto_approved: false,
    execution_type: 'propose',
    tool_name: 'update_budget',
    requested_by_employee_id: 'emp-daan',
    created_at: iso(5 * HOUR),
    updated_at: iso(5 * HOUR),
  },
  {
    id: 'prop-6',
    shop_id: 2,
    shop: { slug: 'nova-sneakers', name: 'Nova Sneakers' },
    action_type_id: 'budget_update',
    action_type: { action_key: 'budget_update', name: 'Budget change' },
    platform: 'google_ads',
    category: 'budget',
    entity_id: 'camp-88',
    entity_name: 'Shopping – Core',
    current_value: { daily_budget: 300, currency: 'EUR' },
    proposed_value: { daily_budget: 345, currency: 'EUR' },
    reasoning:
      'Impression share lost to budget at 31%. A 15% raise recovers top-of-page share on the best-converting terms.',
    change_percentage: 15,
    risk_level: 'medium',
    status: 'pending_review',
    auto_approved: false,
    execution_type: 'platform_api',
    tool_name: 'update_budget',
    requested_by_employee_id: 'emp-daan',
    created_at: iso(6 * HOUR),
    updated_at: iso(6 * HOUR),
  },
];

export const DEMO_PROPOSAL_META = (total: number) => ({
  total,
  per_page: 20,
  current_page: 1,
  last_page: 1,
});

/* -------------------------------- stats --------------------------------- */
export const DEMO_STATS = {
  window: '7d',
  window_start: iso(7 * DAY),
  window_end: iso(0),
  summary: {
    proposals_total: 48,
    proposals_approved: 39,
    proposals_denied: 3,
    proposals_pending: 6,
    proposals_auto_approved: 21,
    auto_approval_rate: 0.44,
    active_employees: 5,
    active_shops: 4,
  },
  time_to_approve_ms: { p50: 420_000, p90: 1_800_000, p99: 5_400_000 },
  proposals_by_platform: [
    { platform: 'facebook_ads', count: 22 },
    { platform: 'google_ads', count: 15 },
    { platform: 'tiktok_ads', count: 7 },
    { platform: 'linkedin_ads', count: 4 },
  ],
  proposals_by_tool: [
    { tool_name: 'update_budget', count: 26 },
    { tool_name: 'update_bids', count: 12 },
    { tool_name: 'pause_adset', count: 10 },
  ],
  executor_success_rate: [
    { platform: 'facebook_ads', success: 21, failure: 1 },
    { platform: 'google_ads', success: 15, failure: 0 },
    { platform: 'tiktok_ads', success: 7, failure: 0 },
  ],
  slack_responsiveness_ms: { p50: 95_000, p90: 540_000, p99: 1_200_000 },
  approved_per_shop: [
    { shop_slug: 'acme', shop_name: 'Acme B.V.', count: 16 },
    { shop_slug: 'nova-sneakers', shop_name: 'Nova Sneakers', count: 11 },
    { shop_slug: 'bloom-cosmetics', shop_name: 'Bloom Cosmetics', count: 8 },
    { shop_slug: 'lumen-audio', shop_name: 'Lumen Audio', count: 4 },
  ],
  risk_distribution: { low: 28, medium: 15, high: 4, critical: 1 },
};

// Scale the base (30d) stats by the requested window so 7d / 30d / 90d differ.
const WINDOW_FACTOR: Record<string, number> = { '7d': 0.26, '30d': 1, '90d': 2.85 };
const WINDOW_DAYS: Record<string, number> = { '7d': 7, '30d': 30, '90d': 90 };

export const statsForWindow = (window: string) => {
  const f = WINDOW_FACTOR[window] ?? 1;
  const days = WINDOW_DAYS[window] ?? 30;
  const n = (x: number) => Math.round(x * f);
  const s = DEMO_STATS;
  return {
    ...s,
    window,
    window_start: iso(days * DAY),
    window_end: iso(0),
    summary: {
      ...s.summary,
      proposals_total: n(s.summary.proposals_total),
      proposals_approved: n(s.summary.proposals_approved),
      proposals_denied: n(s.summary.proposals_denied),
      proposals_pending: n(s.summary.proposals_pending),
      proposals_auto_approved: n(s.summary.proposals_auto_approved),
      auto_approval_rate: Math.min(
        0.92,
        Math.max(0.3, s.summary.auto_approval_rate + (f > 1 ? 0.06 : -0.05))
      ),
    },
    time_to_approve_ms: {
      p50: Math.round(s.time_to_approve_ms.p50 * (f > 1 ? 1.12 : 0.84)),
      p90: Math.round(s.time_to_approve_ms.p90 * (f > 1 ? 1.18 : 0.8)),
      p99: Math.round(s.time_to_approve_ms.p99 * (f > 1 ? 1.22 : 0.76)),
    },
    proposals_by_platform: s.proposals_by_platform.map((p) => ({ ...p, count: n(p.count) })),
    proposals_by_tool: s.proposals_by_tool.map((p) => ({ ...p, count: n(p.count) })),
    executor_success_rate: s.executor_success_rate.map((p) => ({ ...p, success: n(p.success), failure: n(p.failure) })),
    approved_per_shop: s.approved_per_shop.map((p) => ({ ...p, count: n(p.count) })),
    risk_distribution: {
      low: n(s.risk_distribution.low),
      medium: n(s.risk_distribution.medium),
      high: n(s.risk_distribution.high),
      critical: n(s.risk_distribution.critical),
    },
  };
};

/* --------------------------- MCP / governance --------------------------- */
export const DEMO_MCP_CONNECTIONS = [
  {
    id: 1,
    name: 'Facebook Ads',
    type: 'catalog',
    platform_key: 'facebook',
    url: 'https://gateway.adup.io/mcp/meta',
    auth_type: 'oauth',
    status: 'connected',
    last_introspected_at: iso(2 * HOUR),
    last_error: null,
    // Real tool names from the Meta Ads MCP (ads_* namespace).
    tools: [
      { tool_name: 'ads_get_ad_accounts', classification: 'read', policy: 'allow', description: 'List the Meta ad accounts you manage.' },
      { tool_name: 'ads_get_ad_entities', classification: 'read', policy: 'allow', description: 'Read campaigns, ad sets and ads.' },
      { tool_name: 'ads_insights_performance_trend', classification: 'read', policy: 'allow', description: 'Read performance trends and insights.' },
      { tool_name: 'ads_get_opportunity_score', classification: 'read', policy: 'allow', description: 'Read the account opportunity score.' },
      { tool_name: 'ads_activate_entity', classification: 'write', policy: 'allow', description: 'Activate or pause a campaign, ad set or ad.' },
      { tool_name: 'ads_update_entity', classification: 'write', policy: 'require_approval', description: 'Update budget, status or bid on a campaign, ad set or ad.' },
      { tool_name: 'ads_create_ad_set', classification: 'write', policy: 'require_approval', description: 'Create an ad set (budget, audience, schedule).' },
      { tool_name: 'ads_create_campaign', classification: 'write', policy: 'block', description: 'Create a brand-new campaign.' },
    ],
  },
  {
    id: 2,
    name: 'Google Ads',
    type: 'catalog',
    platform_key: 'google_ads',
    url: 'https://gateway.adup.io/mcp/google-ads',
    auth_type: 'oauth',
    status: 'connected',
    last_introspected_at: iso(3 * HOUR),
    last_error: null,
    tools: [
      { tool_name: 'get_campaigns', classification: 'read', policy: 'allow', description: 'Read campaigns and ad groups.' },
      { tool_name: 'get_search_terms', classification: 'read', policy: 'allow', description: 'Read search-term reports.' },
      { tool_name: 'update_budget', classification: 'write', policy: 'require_approval', description: 'Change a campaign budget.' },
      { tool_name: 'update_bids', classification: 'write', policy: 'require_approval', description: 'Adjust bidding / target ROAS.' },
      { tool_name: 'edit_rsa', classification: 'write', policy: 'block', description: 'Edit responsive search ad copy.' },
    ],
  },
  {
    id: 3,
    name: 'Google Analytics',
    type: 'catalog',
    platform_key: 'google_analytics',
    url: 'https://gateway.adup.io/mcp/ga4',
    auth_type: 'oauth',
    status: 'connected',
    last_introspected_at: iso(5 * HOUR),
    last_error: null,
    tools: [
      { tool_name: 'run_report', classification: 'read', policy: 'allow', description: 'Run analytics reports.' },
      { tool_name: 'get_realtime', classification: 'read', policy: 'allow', description: 'Read realtime traffic.' },
      { tool_name: 'get_audiences', classification: 'read', policy: 'allow', description: 'Read configured audiences and segments.' },
    ],
  },
  {
    id: 4,
    name: 'LinkedIn Ads',
    type: 'catalog',
    platform_key: 'linkedin',
    url: 'https://gateway.adup.io/mcp/linkedin',
    auth_type: 'oauth',
    status: 'connected',
    last_introspected_at: iso(6 * HOUR),
    last_error: null,
    tools: [
      { tool_name: 'get_campaigns', classification: 'read', policy: 'allow', description: 'Read campaign groups and campaigns.' },
      { tool_name: 'get_analytics', classification: 'read', policy: 'allow', description: 'Read campaign analytics.' },
      { tool_name: 'update_budget', classification: 'write', policy: 'require_approval', description: 'Change a campaign daily budget.' },
      { tool_name: 'pause_campaign', classification: 'write', policy: 'allow', description: 'Pause a campaign.' },
    ],
  },
  {
    id: 5,
    name: 'TikTok Ads',
    type: 'catalog',
    platform_key: 'tiktok_ads',
    url: 'https://gateway.adup.io/mcp/tiktok',
    auth_type: 'oauth',
    status: 'connected',
    last_introspected_at: iso(7 * HOUR),
    last_error: null,
    tools: [
      { tool_name: 'get_campaigns', classification: 'read', policy: 'allow', description: 'Read campaigns and ad groups.' },
      { tool_name: 'get_reports', classification: 'read', policy: 'allow', description: 'Read performance reports.' },
      { tool_name: 'update_budget', classification: 'write', policy: 'require_approval', description: 'Change an ad group budget.' },
      { tool_name: 'pause_campaign', classification: 'write', policy: 'allow', description: 'Pause a campaign or ad group.' },
    ],
  },
  {
    id: 6,
    name: 'Shopify',
    type: 'catalog',
    platform_key: 'shopify',
    url: 'https://gateway.adup.io/mcp/shopify',
    auth_type: 'oauth',
    status: 'connected',
    last_introspected_at: iso(4 * HOUR),
    last_error: null,
    tools: [
      { tool_name: 'get_orders', classification: 'read', policy: 'allow', description: 'Read orders and fulfilment status.' },
      { tool_name: 'get_products', classification: 'read', policy: 'allow', description: 'Read products and inventory.' },
      { tool_name: 'get_customers', classification: 'read', policy: 'allow', description: 'Read customer records.' },
    ],
  },
  {
    id: 7,
    name: 'Amazon Ads',
    type: 'catalog',
    platform_key: 'amazon_ads',
    url: 'https://gateway.adup.io/mcp/amazon-ads',
    auth_type: 'oauth',
    status: 'connected',
    last_introspected_at: iso(3 * HOUR),
    last_error: null,
    tools: [
      { tool_name: 'get_campaigns', classification: 'read', policy: 'allow', description: 'Read Sponsored Products/Brands campaigns.' },
      { tool_name: 'get_reports', classification: 'read', policy: 'allow', description: 'Read performance and ACOS reports.' },
      { tool_name: 'update_budget', classification: 'write', policy: 'require_approval', description: 'Change a campaign daily budget.' },
      { tool_name: 'pause_campaign', classification: 'write', policy: 'allow', description: 'Pause a campaign or ad group.' },
    ],
  },
  {
    id: 8,
    name: 'Snapchat Ads',
    type: 'catalog',
    platform_key: 'snapchat_ads',
    url: 'https://gateway.adup.io/mcp/snapchat',
    auth_type: 'oauth',
    status: 'connected',
    last_introspected_at: iso(5 * HOUR),
    last_error: null,
    tools: [
      { tool_name: 'get_campaigns', classification: 'read', policy: 'allow', description: 'Read campaigns and ad squads.' },
      { tool_name: 'get_stats', classification: 'read', policy: 'allow', description: 'Read delivery and conversion stats.' },
      { tool_name: 'update_budget', classification: 'write', policy: 'require_approval', description: 'Change an ad squad budget.' },
      { tool_name: 'pause_campaign', classification: 'write', policy: 'allow', description: 'Pause a campaign or ad squad.' },
    ],
  },
  {
    id: 9,
    name: 'Semrush',
    type: 'catalog',
    platform_key: 'semrush',
    url: 'https://gateway.adup.io/mcp/semrush',
    auth_type: 'api_key',
    status: 'connected',
    last_introspected_at: iso(8 * HOUR),
    last_error: null,
    tools: [
      { tool_name: 'domain_overview', classification: 'read', policy: 'allow', description: 'Read domain authority, traffic and keyword counts.' },
      { tool_name: 'keyword_research', classification: 'read', policy: 'allow', description: 'Read keyword volume, difficulty and intent.' },
      { tool_name: 'backlinks_overview', classification: 'read', policy: 'allow', description: 'Read referring domains and backlink profile.' },
      { tool_name: 'competitor_analysis', classification: 'read', policy: 'allow', description: 'Read organic/paid competitors and overlap.' },
    ],
  },
];

/* -------------------------------- audit --------------------------------- */
export const DEMO_AUDIT = [
  { id: 'aud-1', created_at: iso(15 * MIN), actor: 'user:agency:emp-daan', action: 'proposal.approved', shop_slug: 'acme', shop_name: 'Acme B.V.', proposal_id: 'prop-1b', platform: 'facebook_ads', detail: { notes: 'Within ROAS band — approved.' }, risk_level: 'medium' },
  { id: 'aud-2', created_at: iso(22 * MIN), actor: 'system', action: 'proposal.auto_approved', shop_slug: 'acme', shop_name: 'Acme B.V.', proposal_id: 'prop-2', platform: 'google_ads', detail: 'KPI rule: bid shift < 10% while ROAS > 3.0', risk_level: 'low' },
  { id: 'aud-3', created_at: iso(40 * MIN), actor: 'user:agency:emp-sanne', action: 'proposal.created', shop_slug: 'acme', shop_name: 'Acme B.V.', proposal_id: 'prop-1', platform: 'facebook_ads', detail: 'Proposed budget €200 → €230/day (+15%)', risk_level: 'medium' },
  { id: 'aud-4', created_at: iso(2 * HOUR), actor: 'user:agency:emp-daan', action: 'proposal.denied', shop_slug: 'bloom-cosmetics', shop_name: 'Bloom Cosmetics', proposal_id: 'prop-x1', platform: 'tiktok_ads', detail: { reason: 'Exceeds 25% guardrail — revise.' }, risk_level: 'high' },
  { id: 'aud-5', created_at: iso(4 * HOUR), actor: 'system', action: 'proposal.executed', shop_slug: 'nova-sneakers', shop_name: 'Nova Sneakers', proposal_id: 'prop-x2', platform: 'google_ads', detail: 'Budget change applied via platform API.', risk_level: 'low' },
  { id: 'aud-6', created_at: iso(8 * HOUR), actor: 'user:agency:emp-owner-1', action: 'employee.invited', shop_slug: null, shop_name: null, proposal_id: null, platform: null, detail: { email: 'lena@aurora-agency.io', role: 'analyst' }, risk_level: null },
  { id: 'aud-7', created_at: iso(1 * DAY), actor: 'user:agency:emp-mara', action: 'shop.connected', shop_slug: 'lumen-audio', shop_name: 'Lumen Audio', proposal_id: null, platform: 'linkedin_ads', detail: 'Connected LinkedIn Ads via OAuth.', risk_level: null },
  { id: 'aud-8', created_at: iso(1 * DAY + 3 * HOUR), actor: 'system', action: 'proposal.executed', shop_slug: 'acme', shop_name: 'Acme B.V.', proposal_id: 'prop-x3', platform: 'facebook_ads', detail: 'Pause applied.', risk_level: 'low' },
];

export const DEMO_AUDIT_META = {
  total: 32,
  per_page: 25,
  current_page: 1,
  last_page: 2,
};

/* ------------------------- legacy action rules -------------------------- */
// GetAgencyLiveCard only checks `.length > 0` to mark the org "live".
export const DEMO_ACTION_RULES = [
  {
    id: 'rule-1',
    name: 'Auto-approve low-risk budget shifts',
    enabled: true,
    conditions: [{ metric: 'change_percentage', op: '<', value: 10 }, { metric: 'roas', op: '>', value: 3 }],
  },
];

/* ------------------- integrations grid: platforms ----------------------- */
// `tara/mcp/index` (available) — one entry per platform. The original stack uses
// the AdUp CDN logos (production art); the newer performance-marketing
// integrations use the logos bundled at public/mcp-logos/* (their CDN files
// aren't uploaded yet — the live grid falls back to these bundled copies too).
const MCP_LOGO = 'https://adup-media.ams3.cdn.digitaloceanspaces.com/static-data/mcp-logos/';
const BUNDLED_LOGO = `${process.env.PUBLIC_URL || ''}/mcp-logos/`;

// Newer integrations — [slug, display name, bundled logo file, connected?].
const NEW_PLATFORMS: Array<[string, string, string, boolean]> = [
  ['amazon_ads', 'Amazon Ads', 'amazon_ads.png', true],
  ['snapchat_ads', 'Snapchat Ads', 'snapchat_ads.png', true],
  ['semrush', 'Semrush', 'semrush.png', true],
  ['dv360', 'Display & Video 360', 'dv360.png', false],
  ['reddit_ads', 'Reddit Ads', 'reddit_ads.png', false],
  ['x_ads', 'X Ads', 'x_ads.png', false],
  ['outbrain', 'Outbrain', 'outbrain.png', false],
  ['ahrefs', 'Ahrefs', 'ahrefs.png', false],
  ['similarweb', 'Similarweb', 'similarweb.png', false],
  ['supermetrics', 'Supermetrics', 'supermetrics.png', false],
  ['windsor', 'Windsor.ai', 'windsor.png', false],
  ['triplewhale', 'Triple Whale', 'triplewhale.png', false],
  ['mixpanel', 'Mixpanel', 'mixpanel.png', false],
  ['posthog', 'PostHog', 'posthog.png', false],
  ['klaviyo', 'Klaviyo', 'klaviyo.png', false],
  ['activecampaign', 'ActiveCampaign', 'activecampaign.png', false],
  ['appsflyer', 'AppsFlyer', 'appsflyer.png', false],
  ['adjust', 'Adjust', 'adjust.png', false],
  ['stripe', 'Stripe', 'stripe_mcp.png', false],
  ['mollie', 'Mollie', 'mollie.png', false],
  ['bigcommerce', 'BigCommerce', 'bigcommerce.png', false],
  ['magento', 'Magento', 'magento.png', false],
];

export const DEMO_MCP_AVAILABLE = [
  { id: 1, slug: 'facebook', mcp_name: 'Facebook Ads', credentials_fields: [], logo: `${MCP_LOGO}fb_ads.png`, configs: {}, oauth_url: '', is_default: false },
  { id: 2, slug: 'google_ads', mcp_name: 'Google Ads', credentials_fields: [], logo: `${MCP_LOGO}google_ads.png`, configs: {}, oauth_url: '', is_default: false },
  { id: 3, slug: 'google_analytics', mcp_name: 'Google Analytics', credentials_fields: [], logo: `${MCP_LOGO}google-analytics.png`, configs: {}, oauth_url: '', is_default: false },
  { id: 4, slug: 'google_search_console', mcp_name: 'Google Search Console', credentials_fields: [], logo: `${MCP_LOGO}google.png`, configs: {}, oauth_url: '', is_default: false },
  { id: 5, slug: 'hubspot', mcp_name: 'Hubspot', credentials_fields: [], logo: `${MCP_LOGO}hubspot.png`, configs: {}, oauth_url: '', is_default: false },
  { id: 6, slug: 'intercom', mcp_name: 'Intercom', credentials_fields: [], logo: `${MCP_LOGO}intercom.png`, configs: {}, oauth_url: '', is_default: false },
  { id: 7, slug: 'linkedin', mcp_name: 'LinkedIn Ads', credentials_fields: [], logo: `${MCP_LOGO}linkedin.png`, configs: {}, oauth_url: '', is_default: false },
  { id: 8, slug: 'shopify', mcp_name: 'Shopify', credentials_fields: [], logo: `${MCP_LOGO}shopify.png`, configs: {}, oauth_url: '', is_default: false },
  { id: 9, slug: 'tiktok_ads', mcp_name: 'TikTok Ads', credentials_fields: [], logo: `${MCP_LOGO}tiktok_ads.png`, configs: {}, oauth_url: '', is_default: false },
  { id: 10, slug: 'woocommerce', mcp_name: 'WooCommerce', credentials_fields: [], logo: `${MCP_LOGO}woocommerce.png`, configs: {}, oauth_url: '', is_default: false },
  ...NEW_PLATFORMS.map(([slug, name, file], i) => ({
    id: 11 + i,
    slug,
    mcp_name: name,
    credentials_fields: [],
    logo: `${BUNDLED_LOGO}${file}`,
    configs: {},
    oauth_url: '',
    is_default: false,
  })),
];

// `tara/mcp/{slug}/credentials` (connected) — keyed by `tara_mcp_id` = platform id.
// The core stack + the newly-integrated performance channels are connected
// (green "Connected"); the rest have no credential and render the red "Connect".
const NEW_CONNECTED_SLUGS = NEW_PLATFORMS.filter((p) => p[3]).map((p) => p[0]);
const CONNECTED_PLATFORM_IDS = [
  1, 2, 3, 7, 8, 9, // FB, Google Ads, Analytics, LinkedIn, Shopify, TikTok
  ...DEMO_MCP_AVAILABLE.filter((p) => NEW_CONNECTED_SLUGS.includes(p.slug)).map((p) => p.id),
];

export const DEMO_MCP_CREDENTIALS = DEMO_MCP_AVAILABLE.filter((p) =>
  CONNECTED_PLATFORM_IDS.includes(p.id)
).map((p, i) => ({
  id: 100 + p.id,
  connection_id: 100 + p.id,
  tara_mcp_id: p.id,
  is_active: true,
  is_valid: true,
  oauth_required: false,
  credentials: {},
  accounts: { [`act_${1000 + i}`]: `${p.mcp_name} account` },
  tara_mcp: { slug: p.slug },
}));

/* ------------------------- Meta Activity log ---------------------------- */
export const DEMO_META_SUMMARY = {
  total_calls: 1842,
  throttle_events: 3,
  requeue_events: 1,
  peak_buc_usage_pct: 41,
  peak_app_usage_pct: 28,
  by_status: { ok: 1820, throttled: 14, requeued: 6, failed: 2 },
  timeline: Array.from({ length: 7 }, (_, i) => ({
    bucket_ts: iso((6 - i) * DAY),
    calls: 180 + i * 35,
    max_buc_usage_pct: 18 + i * 3,
  })),
};

const META_OPS = [
  { operation: 'campaigns.read', http_method: 'GET', source: 'mcp_read', status: 'ok' },
  { operation: 'insights.read', http_method: 'GET', source: 'mcp_read', status: 'ok' },
  { operation: 'adset.update_budget', http_method: 'POST', source: 'executor_write', status: 'ok' },
  { operation: 'insights.read', http_method: 'GET', source: 'mcp_read', status: 'throttled' },
  { operation: 'campaign.pause', http_method: 'POST', source: 'executor_write', status: 'ok' },
  { operation: 'adset.update_budget', http_method: 'POST', source: 'executor_write', status: 'requeued' },
];

export const DEMO_META_ACTIVITY = Array.from({ length: 18 }, (_, i) => {
  const op = META_OPS[i % META_OPS.length];
  return {
    id: `meta-${i + 1}`,
    created_at: iso(i * 17 * MIN),
    operation: op.operation,
    status: op.status,
    http_method: op.http_method,
    source: op.source,
    shop_name: DEMO_BRANDS[i % DEMO_BRANDS.length].name,
    shop_slug: DEMO_BRANDS[i % DEMO_BRANDS.length].slug,
    ad_account_id: `act_${1000 + (i % 4)}`,
    duration_ms: 120 + (i % 6) * 45,
    buc_usage_pct: 12 + (i % 5) * 6,
    app_usage_pct: 8 + (i % 4) * 5,
    error: op.status === 'failed' ? 'Rate limit reached' : null,
  };
});

export const DEMO_META_META = { total: 64, per_page: 25, current_page: 1, last_page: 3 };

/* ----------------------------- Notifications ---------------------------- */
export const DEMO_PRESENTATION_FREQUENCIES = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' };

export const DEMO_INSIGHTS_AVAILABLE = [
  { id: 'ins-1', metric: 'ROAS drop', short_code: 'roas_drop', description: 'Alert when ROAS drops >15% week-over-week.', is_activated: true, mcp: 'facebook', mcp_info: { slug: 'facebook', name: 'Meta', icon: '' }, shop_config: { frequency: 'daily' } },
  { id: 'ins-2', metric: 'Budget exhausted', short_code: 'budget_exhausted', description: 'Alert when a campaign hits its daily budget before 6pm.', is_activated: true, mcp: 'google_ads', mcp_info: { slug: 'google_ads', name: 'Google Ads', icon: '' }, shop_config: { frequency: 'daily' } },
  { id: 'ins-3', metric: 'Spend spike', short_code: 'spend_spike', description: 'Alert on an unexpected >40% spend spike.', is_activated: false, mcp: 'tiktok_ads', mcp_info: { slug: 'tiktok_ads', name: 'TikTok Ads', icon: '' }, shop_config: { frequency: 'weekly' } },
];

export const DEMO_ALL_ACTIVATED_ITEMS = { items: [], insights_count: 2, prompts_count: 0, graphs_count: 0, total_items: 2 };

export const DEMO_INSIGHT_PREFERENCES = {
  default_notification_method: 'slack',
  delivery_frequency: 'daily',
  monitoring_enabled: true,
};

export const DEMO_DELIVERY_OPTIONS_ACTIVE = [
  { channel: 'slack', config: { value: 'https://hooks.slack.com/services/T0000/B0000/demo-webhook' } },
  { channel: 'email', config: { value: 'team@aurora-agency.io' } },
];

/* ------------------------- per-user stats (Stats tab) ------------------- */
// Counts scale by window so 7/30/90d differ. Analysts propose, managers approve.
const PER_USER_BASE = [
  { emp: 'emp-sanne', proposed: 19, approved: 0, denied: 0 },
  { emp: 'emp-daan', proposed: 4, approved: 23, denied: 2 },
  { emp: 'emp-mara', proposed: 2, approved: 15, denied: 1 },
  { emp: 'emp-lena', proposed: 11, approved: 0, denied: 0 },
  { emp: 'emp-owner-1', proposed: 3, approved: 7, denied: 0 },
  { emp: 'emp-tom', proposed: 0, approved: 0, denied: 0 },
];

export const perUserForWindow = (window: string) => {
  const f = WINDOW_FACTOR[window] ?? 1;
  const days = WINDOW_DAYS[window] ?? 30;
  const n = (x: number) => Math.round(x * f);
  return {
    window,
    window_start: iso(days * DAY),
    window_end: iso(0),
    users: PER_USER_BASE.map((b, i) => {
      const e = DEMO_EMPLOYEES.find((x) => x.id === b.emp);
      const approved = n(b.approved);
      const denied = n(b.denied);
      const reviewed = approved + denied;
      return {
        employee_id: b.emp,
        name: e?.name ?? b.emp,
        email: e?.email ?? '',
        role: e?.role ?? 'analyst',
        proposed_count: n(b.proposed),
        approved_count: approved,
        denied_count: denied,
        approval_rate: reviewed > 0 ? Math.round((approved / reviewed) * 100) / 100 : 0,
        last_active_at: b.emp === 'emp-tom' ? iso(6 * DAY) : iso((i + 1) * 2 * HOUR),
      };
    }),
  };
};

/* ----------------------------- MCP Calls log ---------------------------- */
const MCP_CALL_DEFS = [
  { tool: 'ads_insights_performance_trend', platform: 'facebook', direction: 'read', status: 'ok' },
  { tool: 'get_campaigns', platform: 'google_ads', direction: 'read', status: 'ok' },
  { tool: 'ads_update_entity', platform: 'facebook', direction: 'write', status: 'ok' },
  { tool: 'ads_activate_entity', platform: 'facebook', direction: 'write', status: 'denied' },
  { tool: 'run_report', platform: 'google_analytics', direction: 'read', status: 'ok' },
  { tool: 'update_bids', platform: 'google_ads', direction: 'write', status: 'ok' },
  { tool: 'get_search_terms', platform: 'google_ads', direction: 'read', status: 'error' },
  { tool: 'get_analytics', platform: 'linkedin', direction: 'read', status: 'ok' },
  { tool: 'get_orders', platform: 'shopify', direction: 'read', status: 'ok' },
  { tool: 'pause_campaign', platform: 'tiktok_ads', direction: 'write', status: 'ok' },
];

export const DEMO_MCP_CALLS = Array.from({ length: 22 }, (_, i) => {
  const d = MCP_CALL_DEFS[i % MCP_CALL_DEFS.length];
  const emp = DEMO_EMPLOYEES[(i + 1) % DEMO_EMPLOYEES.length];
  const brand = DEMO_BRANDS[i % DEMO_BRANDS.length];
  return {
    id: `mcpc-${i + 1}`,
    organisation_id: 'org-demo',
    shop_id: brand.id,
    shop_name: brand.name,
    shop_slug: brand.slug,
    employee_id: emp.id,
    employee_name: emp.name,
    platform: d.platform,
    connection_id: 1,
    tool_name: d.tool,
    direction: d.direction,
    status: d.status,
    error_code:
      d.status === 'error' ? 'upstream_error' : d.status === 'denied' ? 'policy_block' : null,
    duration_ms: 90 + (i % 7) * 45,
    created_at: iso(i * 13 * MIN),
  };
});

export const DEMO_MCP_CALLS_META = { total: 58, per_page: 25, current_page: 1, last_page: 3 };

/* ------------------------ tool access (per employee) -------------------- */
export const DEMO_TOOL_ACCESS_EMPLOYEES = DEMO_EMPLOYEES.filter((e) => e.active).map((e) => ({
  id: e.id,
  name: e.name,
  role: e.role,
}));

const humanizeTool = (name: string): string =>
  name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Build the per-employee tool grid from the connected MCP servers' tools.
export const toolAccessFor = (employeeId?: string | null) => {
  const role = employeeId
    ? DEMO_EMPLOYEES.find((e) => e.id === employeeId)?.role ?? null
    : null;
  let policyId = 0;
  const tools: Array<Record<string, unknown>> = [];
  DEMO_MCP_CONNECTIONS.forEach((conn) => {
    conn.tools.forEach((tool) => {
      policyId += 1;
      let enabled = tool.policy !== 'block';
      let overridden = false;
      if (role === 'read_only') {
        enabled = tool.classification === 'read';
        overridden = tool.classification === 'write';
      } else if (role === 'analyst') {
        enabled = tool.classification === 'read' || tool.policy === 'allow';
        overridden = tool.classification === 'write' && tool.policy !== 'allow';
      } else if (role) {
        enabled = tool.policy !== 'block';
      }
      tools.push({
        mcp_tool_policy_id: policyId,
        connection_id: conn.id,
        connection_name: conn.name,
        platform_key: conn.platform_key,
        tool_name: tool.tool_name,
        exposed_name: humanizeTool(tool.tool_name),
        ai_description: tool.description,
        classification: tool.classification,
        connection_policy: tool.policy,
        employee_enabled: enabled,
        is_overridden: overridden,
      });
    });
  });
  return { tools, employees: DEMO_TOOL_ACCESS_EMPLOYEES };
};

/* ---------------------- client-report portal --------------------------- */
// One published-looking monthly report for Acme; the HTML doc lives in
// demo-report-html.ts and is served raw for `client-reports/{id}/html`.
export const DEMO_REPORT_ID = 'rep-2026-06-acme';

const DEMO_REPORT_SUMMARY = {
  id: DEMO_REPORT_ID,
  shop_id: 1,
  shop_slug: 'acme',
  title: 'Acme B.V. — June 2026 performance',
  period_type: 'monthly',
  period_start: '2026-06-01',
  period_end: '2026-06-30',
  period_label: 'June 2026',
  status: 'pending_review',
  current_version_id: 'repv-2',
  created_by_type: 'ai',
  created_by: null,
  source_metadata: { channels: ['google_ads', 'facebook', 'tiktok_ads'] },
  reviewed_by: null,
  reviewed_at: null,
  review_notes: null,
  published_at: null,
  client_notified_at: null,
  created_at: iso(2 * DAY),
  updated_at: iso(3 * HOUR),
};

export const DEMO_REPORT_DETAIL = {
  ...DEMO_REPORT_SUMMARY,
  versions: [
    { id: 'repv-1', report_id: DEMO_REPORT_ID, version_number: 1, html_sha256: null, size_bytes: 48213, edited_by: null, edit_summary: 'Initial AI-generated draft', sanitizer_report: null, created_at: iso(2 * DAY) },
    { id: 'repv-2', report_id: DEMO_REPORT_ID, version_number: 2, html_sha256: null, size_bytes: 48620, edited_by: 'emp-mara', edit_summary: 'Tightened the exec summary + fixed the non-brand ROAS note', sanitizer_report: null, created_at: iso(3 * HOUR) },
  ],
  audit_tail: [
    { id: 'repa-1', actor_type: 'ai', actor_id: null, action: 'report.generated', metadata: { channels: 3 }, created_at: iso(2 * DAY) },
    { id: 'repa-2', actor_type: 'employee', actor_id: 'emp-mara', action: 'report.edited', metadata: { version: 2 }, created_at: iso(3 * HOUR) },
  ],
};

// Save/restore echoes an incremented version.
export const nextReportVersion = () => ({
  data: { id: 'repv-3', report_id: DEMO_REPORT_ID, version_number: 3, html_sha256: null, size_bytes: 48700, edited_by: 'emp-owner-1', edit_summary: 'Manual edit', sanitizer_report: { removed_scripts: 0, removed_event_handlers: 0, removed_js_urls: 0, removed_embeds: 0 }, created_at: iso(0) },
  report: { ...DEMO_REPORT_SUMMARY, current_version_id: 'repv-3', updated_at: iso(0) },
});

// The reports list (GET /client-reports). All open the same sample report
// (the detail/html routes return the June-Acme fixture for any id).
export const DEMO_REPORTS_LIST = [
  { ...DEMO_REPORT_SUMMARY },
  {
    ...DEMO_REPORT_SUMMARY,
    id: 'rep-2026-05-acme',
    title: 'Acme B.V. — May 2026 performance',
    period_start: '2026-05-01',
    period_end: '2026-05-31',
    period_label: 'May 2026',
    status: 'published',
    published_at: iso(30 * DAY),
    created_at: iso(34 * DAY),
    updated_at: iso(30 * DAY),
  },
  {
    ...DEMO_REPORT_SUMMARY,
    id: 'rep-2026-06-nova',
    shop_id: 2,
    shop_slug: 'nova-sneakers',
    title: 'Nova Sneakers — June 2026 performance',
    status: 'approved',
    created_at: iso(1 * DAY),
    updated_at: iso(20 * HOUR),
  },
];

type DemoMetric = {
  id: string;
  /** null = platform global; non-null = org-custom (created in the demo). */
  organisation_id: string | null;
  key: string;
  label: string;
  description: string | null;
  unit: string;
  direction: string;
  aggregation: string;
  platforms: string[] | null;
  synonyms: string[] | null;
};

let DEMO_METRICS_MUTABLE: DemoMetric[] = [
  { id: 'm-roas', organisation_id: null, key: 'roas', label: 'ROAS', description: 'Return on ad spend', unit: 'ratio', direction: 'higher_better', aggregation: 'ratio', platforms: null, synonyms: ['return on ad spend'] },
  { id: 'm-cpa', organisation_id: null, key: 'cpa', label: 'CPA', description: 'Cost per acquisition', unit: 'currency', direction: 'lower_better', aggregation: 'avg', platforms: null, synonyms: null },
  { id: 'm-revenue', organisation_id: null, key: 'revenue', label: 'Revenue', description: 'Attributed revenue', unit: 'currency', direction: 'higher_better', aggregation: 'sum', platforms: null, synonyms: null },
  { id: 'm-spend', organisation_id: null, key: 'spend', label: 'Ad spend', description: 'Media spend', unit: 'currency', direction: 'lower_better', aggregation: 'sum', platforms: null, synonyms: null },
  { id: 'm-conv', organisation_id: null, key: 'conversions', label: 'Conversions', description: 'Purchase conversions', unit: 'count', direction: 'higher_better', aggregation: 'sum', platforms: null, synonyms: null },
  { id: 'm-aov', organisation_id: null, key: 'aov', label: 'AOV', description: 'Average order value', unit: 'currency', direction: 'higher_better', aggregation: 'avg', platforms: null, synonyms: null },
  { id: 'm-ctr', organisation_id: null, key: 'ctr', label: 'CTR', description: 'Click-through rate', unit: 'percent', direction: 'higher_better', aggregation: 'avg', platforms: null, synonyms: null },
  { id: 'm-cpl', organisation_id: null, key: 'cpl', label: 'CPL', description: 'Cost per lead', unit: 'currency', direction: 'lower_better', aggregation: 'avg', platforms: null, synonyms: null },
  { id: 'm-leads', organisation_id: null, key: 'leads', label: 'Leads', description: 'Lead conversions', unit: 'count', direction: 'higher_better', aggregation: 'sum', platforms: null, synonyms: null },
  { id: 'm-cvr', organisation_id: null, key: 'conversion_rate', label: 'Conversion rate', description: 'Share of clicks that convert', unit: 'percent', direction: 'higher_better', aggregation: 'avg', platforms: null, synonyms: ['cvr'] },
];

// NB: read through listMetrics() — a plain `export const` would freeze the
// binding to the initial array and miss anything createMetric() appends.
export const listMetrics = () => DEMO_METRICS_MUTABLE.map((m) => ({ ...m }));

/** Mirrors POST /metrics — appends an org-custom metric to the vocabulary. */
export const createMetric = (body: Record<string, unknown>): DemoMetric => {
  const row: DemoMetric = {
    id: `m-custom-${DEMO_METRICS_MUTABLE.length + 1}`,
    organisation_id: 'org-demo',
    key: String(body.key ?? ''),
    label: String(body.label ?? ''),
    description: (body.description as string | null) ?? null,
    unit: String(body.unit ?? 'currency'),
    direction: String(body.direction ?? 'higher_better'),
    aggregation: String(body.aggregation ?? 'sum'),
    platforms: (body.platforms as string[] | null) ?? null,
    synonyms: (body.synonyms as string[] | null) ?? null,
  };
  DEMO_METRICS_MUTABLE = [...DEMO_METRICS_MUTABLE, row];
  return row;
};

let kpiTargets = [
  { id: 'kt-1', shop_id: 1, metric_key: 'roas', platform: 'blended', period_type: 'month', period_start: '2026-07-01', target_value: 4, target_type: 'target', currency: 'EUR', notes: 'Floor 3.5x, target 4x', created_at: iso(5 * DAY), updated_at: iso(5 * DAY) },
  { id: 'kt-2', shop_id: 1, metric_key: 'cpa', platform: 'blended', period_type: 'month', period_start: '2026-07-01', target_value: 24, target_type: 'cap', currency: 'EUR', notes: null, created_at: iso(5 * DAY), updated_at: iso(5 * DAY) },
  { id: 'kt-3', shop_id: 1, metric_key: 'roas', platform: 'google', period_type: 'month', period_start: '2026-07-01', target_value: 4.2, target_type: 'target', currency: 'EUR', notes: null, created_at: iso(5 * DAY), updated_at: iso(5 * DAY) },
  { id: 'kt-4', shop_id: 1, metric_key: 'roas', platform: 'meta', period_type: 'month', period_start: '2026-07-01', target_value: 4, target_type: 'target', currency: 'EUR', notes: null, created_at: iso(5 * DAY), updated_at: iso(5 * DAY) },
];
export const listKpiTargets = () => kpiTargets.map((t) => ({ ...t }));
export const upsertKpiTarget = (body: Record<string, unknown>) => {
  const row = {
    id: `kt-${kpiTargets.length + 1}`,
    shop_id: 1,
    currency: 'EUR',
    notes: null,
    created_at: iso(0),
    updated_at: iso(0),
    ...body,
  } as (typeof kpiTargets)[number];
  kpiTargets = [...kpiTargets.filter((t) => t.id !== row.id), row];
  return row;
};

export const DEMO_REPORT_STYLE_PRESETS = [
  { key: 'midnight', name: 'Midnight', description: 'Dark, high-contrast decks', preview_thumbnail_path: null, default_tokens: { mode: 'dark', colors: { bg: '#0b1020', panel: '#141a2e', accent: '#ffd84d', accent_2: '#5b9bff', text: '#eef2ff', text_muted: '#9aa6c4' }, fonts: { heading: 'Inter', body: 'Inter' } }, is_active: true },
  { key: 'daylight', name: 'Daylight', description: 'Clean, printable light report', preview_thumbnail_path: null, default_tokens: { mode: 'light', colors: { bg: '#ffffff', panel: '#f7f8fb', accent: '#4f46e5', accent_2: '#0ea5e9', text: '#0b1220', text_muted: '#64748b' }, fonts: { heading: 'Inter', body: 'Inter' } }, is_active: true },
];

export const DEMO_REPORT_BRANDING = [
  { id: 'rb-default', organisation_id: 'org-demo', shop_id: null, shop_slug: null, preset_key: 'midnight', design_md_override: null, tokens: DEMO_REPORT_STYLE_PRESETS[0].default_tokens, logo_light_path: null, logo_dark_path: null, favicon_path: null, font_assets: null, is_default: true, is_active: true, created_at: iso(20 * DAY), updated_at: iso(6 * DAY) },
];

export const DEMO_CLIENT_PORTAL_SETTINGS = {
  changelog_enabled: true,
  changelog_categories: ['budget', 'creative', 'targeting'],
  changelog_lookback_days: 30,
  deep_dive_enabled: true,
  template_report_id: DEMO_REPORT_ID,
};

export const DEMO_AGENCY_DOMAINS = [
  { id: 'dom-1', hostname: 'acme.r.adup.io', type: 'subdomain', status: 'active', is_primary: true, tls_provider: 'letsencrypt', tls_status: 'active', verified_at: iso(15 * DAY), created_at: iso(20 * DAY), verification_record: null },
];
